import { useMemo, type ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TooltipProvider } from '@r/components/ui/tooltip.tsx';
import { cn } from '@r/utils/utils.ts';
import type {
  ItemDefinition,
  SlideFormValues
} from '@admin/pages/personalization/content-block-slides.ts';
import { getSlideFormLayoutDefinitions } from '@admin/components/personalization/content-block-slide-form-layout.ts';
import type { TextColorPreset } from '@admin/components/personalization/content-block-slide-form-field-utils.ts';
import { ContentBlockSlideDefinitionField } from '@admin/components/personalization/content-block-slide-form-definition-field.tsx';

interface ContentBlockSlideFormFieldsProps {
  itemDefinitions: ItemDefinition[];
  form: UseFormReturn<SlideFormValues>;
  progresses: Record<string, number> | undefined;
  mediaFieldDisabled: boolean;
  onMediaUpload: (
    itemKey: string,
    files: File[],
    onChange: (value: string) => void
  ) => void | Promise<void>;
}

/** Champs de la slide courante, layout type maquette (sections visibles). */
export const ContentBlockSlideFormFields = ({
  itemDefinitions,
  form,
  progresses,
  mediaFieldDisabled,
  onMediaUpload
}: ContentBlockSlideFormFieldsProps) => {
  const { t } = useTranslation();

  const textColorPresets = useMemo(
    (): TextColorPreset[] => [
      {
        id: 'white',
        value: 'hsl(0, 0%, 100%)',
        label: t('admin.contentblock.editor.text_color.white'),
        cssBackground: 'hsl(0, 0%, 100%)'
      },
      {
        id: 'black',
        value: 'hsl(0, 0%, 0%)',
        label: t('admin.contentblock.editor.text_color.black'),
        cssBackground: 'hsl(0, 0%, 0%)'
      }
    ],
    [t]
  );

  const layout = getSlideFormLayoutDefinitions(itemDefinitions);

  const sharedFieldProps = {
    form,
    t,
    textColorPresets,
    progresses,
    mediaFieldDisabled,
    onMediaUpload
  };

  const renderField = (itemDefinition: ItemDefinition) => (
    <ContentBlockSlideDefinitionField
      key={itemDefinition.itemKey}
      itemDefinition={itemDefinition}
      {...sharedFieldProps}
    />
  );

  const backgroundImagesGrid = (): ReactNode => {
    if (!layout.backgroundImageDef && !layout.backgroundImageMobileDef) return null;

    return (
      <div
        className={cn(
          'grid gap-6',
          layout.backgroundImageDef && layout.backgroundImageMobileDef
            ? 'md:grid-cols-2'
            : 'grid-cols-1'
        )}
      >
        {layout.backgroundImageDef ? renderField(layout.backgroundImageDef) : null}
        {layout.backgroundImageMobileDef ? renderField(layout.backgroundImageMobileDef) : null}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-8">
        {layout.descriptionDef ? renderField(layout.descriptionDef) : null}

        {(layout.textCtaDef || layout.externLinkDef) && (
          <div
            className={cn(
              'grid gap-6',
              layout.textCtaDef && layout.externLinkDef ? 'md:grid-cols-2' : 'grid-cols-1'
            )}
          >
            {layout.textCtaDef ? renderField(layout.textCtaDef) : null}
            {layout.externLinkDef ? renderField(layout.externLinkDef) : null}
          </div>
        )}

        {(layout.textColorDef || layout.ctaPositionDef) && (
          <div
            className={cn(
              'grid gap-6',
              layout.textColorDef && layout.ctaPositionDef ? 'md:grid-cols-2' : 'grid-cols-1'
            )}
          >
            {layout.textColorDef ? renderField(layout.textColorDef) : null}
            {layout.ctaPositionDef ? renderField(layout.ctaPositionDef) : null}
          </div>
        )}

        {backgroundImagesGrid()}

        {layout.filterDef ? renderField(layout.filterDef) : null}

        {layout.orderedDefinitions.map((itemDefinition) => {
          if (layout.skipKeys.has(itemDefinition.itemKey)) return null;
          return renderField(itemDefinition);
        })}
      </div>
    </TooltipProvider>
  );
};
